/**
 * RelationPicker — F12.C T12.C.1 + część B2 mechaniki referencji (Kreator modułów).
 *
 * Dropdown z wyszukiwarką po rekordach powiązanego źródła. Dwa tryby źródła danych:
 *
 *  1. **Moduł custom** (`moduleId`) — domyślny. Pyta generyczny endpoint rekordów modułu
 *     (`/modules/:id/records`), kształt `{ id, record_name, data }`.
 *  2. **Kartoteka** (`systemModuleSlug`) — gdy pole odwołuje się do encji systemowej
 *     (pracownicy / kontrahenci / użytkownicy). Pyta dedykowany endpoint kartoteki wg
 *     mapy adapterów (`lib/system-entity-adapters.ts`); każda kartoteka ma własny endpoint,
 *     parametr szukajki i kształt odpowiedzi. Wybrany rekord (przy zamkniętym pickerze)
 *     dociągany batch-em po id gdy adapter ma `resolveByIds` (dziś: `documents`), inaczej per-rekord przez endpoint szczegółu.
 *
 * Tryby wykluczają się: gdy podany `systemModuleSlug`, `moduleId` jest ignorowane.
 *
 * Adaptacja z SellCall RelationPicker (uproszczona — bez inline create, bez filters,
 * bez displayTemplate, bez sortField). Używa naszego `api` klienta.
 *
 * T3 (company scoping, wave „pola dodatkowe na listach i picker"): kartoteka
 * `users` w trybie SINGLE-select (`!multiple`) delegowana do wspólnego
 * `UserPicker` (avatar + dwie linie, scoping `?companyId=`) — jeden wspólny
 * komponent usera na wszystkich kartotekach, konsystentny z owner_user_id na
 * fakturach/umowach/pismach. Multi-select ZOSTAJE na generycznej ścieżce
 * kartoteki niżej — `UserPicker` nie wspiera wielokrotnego wyboru, a relacja
 * z dwoma przypisanymi userami nie może po zmianie silently zgubić jednego.
 *
 * Etykieta wybranego usera w delegacji: `selectedFetched` (adapter `detailPath`
 * — dokładnie ta ścieżka, którą ten komponent miał PRZED delegacją) zostaje
 * WŁĄCZONA nawet dla `isSingleUserRelation`, żeby zachować parytet — resolwowana
 * etykieta idzie do `UserPicker` jako `selectedLabel` (jego własny fallback
 * `GET /users/:id` wygasza się wtedy automatycznie, zero duplikującego fetcha).
 *
 * `companyId` — poza zasileniem delegacji do `UserPicker`, jest też przekazywany
 * do generycznej listy kartoteki (`listOptions`, `?companyId=`) dla WSZYSTKICH
 * adapterów, które go honorują po stronie serwera (dziś: contractors, employees,
 * products — `resolveListCompanyScope`). Adaptery bez wsparcia po prostu
 * ignorują nieznany query param (standardowe zachowanie Next.js route handlerów).
 */

import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, ExternalLink, Loader2, Pencil, Plus, Search, X } from 'lucide-react';

import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { UserPicker } from '@/components/ui/UserPicker';
import { api } from '@/lib/api';
import { getSystemEntityAdapter } from '@/lib/system-entity-adapters';

interface RelatedRecord {
  id: string;
  record_name: string | null;
  data: Record<string, unknown>;
}

/** Znormalizowana opcja do wyświetlenia — wspólny kształt dla obu trybów źródła. */
interface PickerOption {
  id: string;
  label: string;
}

/**
 * Akcje pomocnicze okna wyboru (opt-in) — „okno wyboru kontrahenta" na umowach/pismach
 * (spotkanie FK 31.08). Gdy podane, dropdown renderuje per-opcja ikony „otwórz kartotekę"
 * (`onOpenCard`) i „edytuj" (`onEditRecord`) oraz przycisk „dodaj nowego" (`onCreateNew`)
 * w stopce listy. Klik akcji NIE przełącza zaznaczenia (stopPropagation). Etykiety
 * dostarcza KONSUMENT (RelationPicker jest generyczny — kontrahent/moduł/user mają inne
 * teksty). Pominięty handler = brak danej akcji. Brak propa → zachowanie bez zmian.
 */
export interface RelationPickerActions {
  onOpenCard?: (id: string) => void;
  onEditRecord?: (id: string) => void;
  onCreateNew?: () => void;
  labels: {
    openCard: string;
    editRecord: string;
    createNew: string;
  };
}

interface RelationPickerProps {
  /** Tryb modułu custom — id powiązanego modułu. Ignorowane gdy `systemModuleSlug` podany. */
  moduleId?: string;
  /** Tryb kartoteki — slug encji systemowej (pracownicy/kontrahenci/użytkownicy). */
  systemModuleSlug?: string | null;
  value: string | string[] | null;
  onChange: (value: string | string[] | null) => void;
  multiple?: boolean;
  displayField?: string;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Firma dla scopingu — przekazana JAKO `?companyId=` do generycznej listy
   * kartoteki (adaptery, które go honorują po stronie serwera: contractors/
   * employees/products) ORAZ do `UserPicker` gdy `systemModuleSlug='users'` i
   * `!multiple` (patrz nagłówek pliku). `undefined`/`null` = picker
   * nieograniczony (boundary rule CLAUDE.md §4.8).
   */
  companyId?: string | null;
  /** Akcje pomocnicze okna wyboru (otwórz/edytuj/dodaj) — patrz `RelationPickerActions`. */
  recordActions?: RelationPickerActions;
  'data-testid'?: string;
}

/** Smart display dla rekordu modułu custom: record_name → pierwsze pole tekstowe → prefix UUID */
function formatModuleDisplay(record: RelatedRecord, displayField = 'record_name'): string {
  if (displayField !== 'record_name') {
    const v = record.data[displayField];
    if (v != null && String(v).trim()) return String(v);
  }
  if (record.record_name) return record.record_name;
  const d = record.data;
  const candidates = ['nazwa', 'name', 'tytul', 'tytuł', 'title', 'numer', 'nr'];
  for (const k of candidates) {
    if (d[k] && String(d[k]).trim()) return String(d[k]);
  }
  for (const v of Object.values(d)) {
    if (typeof v === 'string' && v.trim().length > 0 && v.length < 120) return v;
  }
  return record.id.slice(0, 8) + '…';
}

export function RelationPicker({
  moduleId,
  systemModuleSlug,
  value,
  onChange,
  multiple = false,
  displayField = 'record_name',
  placeholder = 'Wybierz rekord…',
  disabled = false,
  companyId,
  recordActions,
  'data-testid': testId,
}: RelationPickerProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const adapter = getSystemEntityAdapter(systemModuleSlug);
  // Tryb kartoteki jest aktywny tylko gdy slug jest obsługiwany przez adapter.
  const isKartoteka = !!adapter;
  // Źródło danych jest dostępne, gdy mamy adapter kartoteki albo id modułu custom.
  const hasSource = isKartoteka || !!moduleId;
  const sourceKey = isKartoteka ? `kartoteka:${adapter.slug}` : `module:${moduleId ?? ''}`;
  // T3: delegacja do UserPicker (patrz nagłówek pliku) — wyłącznie users + single-select.
  const isSingleUserRelation = isKartoteka && adapter.slug === 'users' && !multiple;

  const selectedIds = useMemo<string[]>(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  // Lista opcji do dropdownu (gdy otwarty).
  const { data: listOptions, isLoading } = useQuery<PickerOption[]>({
    queryKey: ['relation-records', sourceKey, search, companyId ?? null],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '30' });
      if (isKartoteka) {
        if (search) params.set(adapter.searchParam, search);
        if (companyId) params.set('companyId', companyId);
        const res = await api.get<Record<string, unknown>>(
          `${adapter.listPath}?${params.toString()}`,
        );
        const rows = (res.data[adapter.listResponseKey] as Record<string, unknown>[]) ?? [];
        return rows.map((r) => ({ id: String(r.id), label: adapter.getLabel(r) }));
      }
      if (search) params.set('search', search);
      const res = await api.get<{ records: RelatedRecord[] }>(
        `/modules/${moduleId}/records?${params.toString()}`,
      );
      return (res.data.records ?? []).map((r) => ({
        id: r.id,
        label: formatModuleDisplay(r, displayField),
      }));
    },
    // Delegacja do UserPicker (users, single-select) renderuje WŁASNY dropdown —
    // ta lista nigdy się nie wyświetla dla tej gałęzi, więc nie fetchujemy jej.
    enabled: isOpen && hasSource && !isSingleUserRelation,
    staleTime: 15_000,
  });

  // Pre-fetch wybranych rekordów (etykiety przy zamkniętym pickerze). ZOSTAJE
  // WŁĄCZONE nawet dla `isSingleUserRelation` (patrz nagłówek pliku) — resolwuje
  // etykietę przez `adapter.detailPath`, przekazywaną do `UserPicker` jako
  // `selectedLabel` (restore parity — ta ścieżka istniała PRZED delegacją).
  const { data: selectedFetched, isLoading: selectedFetchedLoading } = useQuery<PickerOption[]>({
    queryKey: ['relation-selected', sourceKey, ...selectedIds],
    queryFn: async () => {
      if (selectedIds.length === 0) return [];
      if (isKartoteka) {
        // Adaptery z `resolveByIds` (dziś: WYŁĄCZNIE `documents`) dociągają batch-em w
        // jednym zapytaniu — mirror `RelationCellLabel`/`RecordTablePicker` prefetch.
        if (adapter.resolveByIds) {
          try {
            const res = await api.get<Record<string, unknown>>(
              adapter.resolveByIds.path(selectedIds),
            );
            const rows =
              (res.data[adapter.resolveByIds.responseKey] as
                | Record<string, unknown>[]
                | undefined) ?? [];
            return rows.map((r) => ({ id: String(r.id), label: adapter.getLabel(r) }));
          } catch {
            return [];
          }
        }
        // Kartoteki bez `resolveByIds` nie mają „pobierz po idkach" — dociągamy każdy
        // rekord ze szczegółu.
        const fetched = await Promise.all(
          selectedIds.map(async (id) => {
            try {
              if (!adapter.detailPath || !adapter.detailResponseKey) return null;
              const res = await api.get<Record<string, unknown>>(adapter.detailPath(id));
              const raw = res.data[adapter.detailResponseKey] as
                | Record<string, unknown>
                | undefined;
              return raw ? { id: String(raw.id ?? id), label: adapter.getLabel(raw) } : null;
            } catch {
              return null;
            }
          }),
        );
        return fetched.filter((o): o is PickerOption => o !== null);
      }
      const params = new URLSearchParams({ ids: selectedIds.join(',') });
      const res = await api.get<{ records: RelatedRecord[] }>(
        `/modules/${moduleId}/records?${params.toString()}`,
      );
      return (res.data.records ?? []).map((r) => ({
        id: r.id,
        label: formatModuleDisplay(r, displayField),
      }));
    },
    enabled: selectedIds.length > 0 && hasSource,
    staleTime: 60_000,
  });

  const options = useMemo<PickerOption[]>(() => listOptions ?? [], [listOptions]);
  const selectedOptions: PickerOption[] = useMemo(() => {
    const knownAll = [...options, ...(selectedFetched ?? [])];
    const seen = new Set<string>();
    return selectedIds
      .map((id) => knownAll.find((o) => o.id === id) ?? { id, label: id.slice(0, 8) + '…' })
      .filter((o) => {
        if (seen.has(o.id)) return false;
        seen.add(o.id);
        return true;
      });
  }, [selectedIds, options, selectedFetched]);

  function toggleRecord(id: string) {
    if (multiple) {
      const cur = Array.isArray(value) ? value : [];
      onChange(cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
    } else {
      onChange(id);
      setIsOpen(false);
      setSearch('');
    }
  }

  function removeRecord(id: string, e: React.MouseEvent) {
    e.stopPropagation();
    if (multiple) {
      const cur = Array.isArray(value) ? value : [];
      onChange(cur.filter((x) => x !== id));
    } else {
      onChange(null);
    }
  }

  function openPicker() {
    if (disabled) return;
    setIsOpen(true);
    setTimeout(() => searchRef.current?.focus(), 50);
  }

  // T3: users + single-select → wspólny UserPicker (patrz nagłówek pliku). Umieszczone
  // PO wszystkich hookach powyżej (useMemo/useQuery zawsze wołane, niezależnie od tej
  // gałęzi) — zero warunkowych wywołań hooków, tylko wybór JSX do zwrócenia.
  if (isSingleUserRelation) {
    // Etykieta rozwiązana przez `selectedFetched` (adapter `detailPath`, WŁĄCZONE
    // powyżej nawet dla tej gałęzi) — restore parity: ta ścieżka istniała PRZED
    // delegacją, przekazujemy ją jako `selectedLabel` żeby UserPicker nigdy nie
    // musiał sam dociągać (i nigdy nie zbłankował etykiety w międzyczasie).
    // `selectedLabelPending` — ten fetch jest JESZCZE w toku (`resolvedLabel` byłby
    // chwilowo `null`, nie „nieobecny") — wygasza wewnętrzny fallback UserPickera na
    // czas trwania, żeby nie strzelić duplikującym `GET /users/:id`.
    const resolvedLabel =
      typeof value === 'string'
        ? (selectedFetched?.find((o) => o.id === value)?.label ?? null)
        : null;
    return (
      <UserPicker
        value={typeof value === 'string' ? value : null}
        onChange={(v) => onChange(v)}
        companyId={companyId}
        selectedLabel={resolvedLabel}
        selectedLabelPending={selectedFetchedLoading}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
      />
    );
  }

  return (
    <div className="relative" data-testid={testId}>
      {/* Trigger */}
      <div
        role="combobox"
        aria-expanded={isOpen}
        tabIndex={disabled ? -1 : 0}
        onClick={openPicker}
        onKeyDown={(e) => e.key === 'Enter' && openPicker()}
        className={[
          'min-h-[38px] w-full cursor-pointer rounded-md border px-3 py-2 text-sm transition',
          'bg-bg-primary text-text-primary',
          disabled
            ? 'cursor-not-allowed opacity-50 border-border-primary'
            : 'border-border-primary hover:border-accent-400 focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500',
        ].join(' ')}
      >
        {selectedOptions.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 pr-6">
            {selectedOptions.map((o) => (
              <span
                key={o.id}
                className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-700"
              >
                {o.label}
                {!disabled && (
                  <IconButton
                    aria-label={t('common.multiSelect.removeAriaLabel', { label: o.label })}
                    variant="ghost"
                    size="sm"
                    onClick={(e) => removeRecord(o.id, e)}
                    className="h-auto w-auto p-0 hover:text-accent-900"
                    tabIndex={-1}
                  >
                    <X className="h-3 w-3" />
                  </IconButton>
                )}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-text-muted">{placeholder}</span>
        )}
        <ChevronDown
          className={`absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => {
              setIsOpen(false);
              setSearch('');
            }}
          />
          <div className="absolute z-50 mt-1 w-full rounded-lg border border-border-primary bg-bg-primary shadow-lg">
            {/* Search */}
            <div className="p-2 border-b border-border-primary">
              <Input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Szukaj…"
                prefixIcon={<Search className="h-3.5 w-3.5" />}
                containerClassName="gap-0"
                className="py-1.5 text-sm"
                data-testid={testId ? `${testId}-search` : undefined}
              />
            </div>

            {/* Records */}
            <div className="max-h-60 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-4 w-4 animate-spin text-text-muted" />
                </div>
              ) : options.length === 0 ? (
                <p className="p-3 text-center text-sm text-text-muted">
                  {search ? 'Brak wyników' : 'Brak rekordów'}
                </p>
              ) : (
                options.map((option) => {
                  const isSelected = selectedIds.includes(option.id);
                  return (
                    <div
                      key={option.id}
                      onClick={() => toggleRecord(option.id)}
                      className={[
                        'flex cursor-pointer items-center justify-between px-3 py-2 text-sm transition',
                        isSelected
                          ? 'bg-accent-50 text-accent-700'
                          : 'text-text-primary hover:bg-bg-secondary',
                      ].join(' ')}
                      data-testid={testId ? `${testId}-option-${option.id}` : undefined}
                    >
                      <span className="truncate">{option.label}</span>
                      <div className="flex flex-shrink-0 items-center gap-1">
                        {recordActions?.onOpenCard && (
                          <IconButton
                            size="sm"
                            variant="ghost"
                            aria-label={recordActions.labels.openCard}
                            title={recordActions.labels.openCard}
                            tabIndex={-1}
                            onClick={(e) => {
                              e.stopPropagation();
                              recordActions.onOpenCard?.(option.id);
                            }}
                            data-testid={testId ? `${testId}-open-${option.id}` : undefined}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </IconButton>
                        )}
                        {recordActions?.onEditRecord && (
                          <IconButton
                            size="sm"
                            variant="ghost"
                            aria-label={recordActions.labels.editRecord}
                            title={recordActions.labels.editRecord}
                            tabIndex={-1}
                            onClick={(e) => {
                              e.stopPropagation();
                              recordActions.onEditRecord?.(option.id);
                            }}
                            data-testid={testId ? `${testId}-edit-${option.id}` : undefined}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </IconButton>
                        )}
                        {isSelected && <Check className="h-4 w-4 text-accent-600" />}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            {recordActions?.onCreateNew && (
              <div className="border-t border-border-primary p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-center"
                  leftIcon={<Plus className="h-3.5 w-3.5" />}
                  onClick={() => recordActions.onCreateNew?.()}
                  data-testid={testId ? `${testId}-create-new` : undefined}
                >
                  {recordActions.labels.createNew}
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
